<script lang="ts">
	/* eslint-disable svelte/no-at-html-tags */

	import { groupBy } from 'fp-ts/lib/ReadonlyNonEmptyArray';

	import type { PageData } from './$types';
	import HandwrittenHeader from '$lib/components/HandwrittenHeader.svelte';
	import { locale, path, t } from '$lib/translations';
	import { ƒ_thought, ƒ_thoughts } from '$lib/routes';
	import { formatAsLong } from '$lib/date-and-time';
	import { yearMonthDaySlug } from '$lib/domain/thought';
	import { fly } from 'svelte/transition';
	import { cubicIn, cubicOut } from 'svelte/easing';
	import ThoughtPermalink from '$lib/components/thought/ThoughtPermalink.svelte';

	interface Props {
		data: PageData;
	}

	let { data }: Props = $props();

	let { thoughts } = $derived(data);

	let decoratedThoughtsByYear = $derived(groupBy((thought: any) => String(new Date(thought._firstPublishedAt).getFullYear()))(thoughts));

	let sortedYears = $derived(Object.keys(decoratedThoughtsByYear).sort((a, b) => parseInt(b, 10) - parseInt(a, 10)));

	let firstThought = $derived(thoughts[thoughts.length - 1]);
	let lastThought = $derived(thoughts[0]);

	let formatToLocaleAsLong = $derived(formatAsLong($locale));

	let startDate = $derived(formatToLocaleAsLong(new Date(firstThought._firstPublishedAt)));
	let endDate = $derived(formatToLocaleAsLong(new Date(lastThought._firstPublishedAt)));

	let firstThoughtPath = $derived($path(ƒ_thought, yearMonthDaySlug(firstThought)));
	let lastThoughtPath = $derived($path(ƒ_thought, yearMonthDaySlug(lastThought)));

	const duration = 300;
	const y = 100;

	// See: https://joshcollinsworth.com/blog/great-transitions
	const transitionIn = () => ({ easing: cubicOut, y: -y, duration, delay: 400 });
	const transitionOut = () => ({ easing: cubicIn, y, duration });
</script>

<svelte:head>
	<title>{$t('thoughts.sparseThoughts')} — sistrall.it</title>
</svelte:head>

<div class="px-2 md:px-4 lg:px-8 mx-auto">
	<HandwrittenHeader />
	<div class="font-sans font-bold uppercase border-b-4 inline-block ml-2 md:ml-4 lg:ml-8">
		<a href={$path(ƒ_thoughts)}>{$t('thoughts.countSparseThoughts', { count: data.count })}</a>
	</div>
	<div class="font-sans font-thin border-b-4 inline-block ml-2 md:ml-4 lg:ml-8">
		{@html $t('thoughts.publishedFromTo', { from: `<a href=${firstThoughtPath}>${startDate}</a>`, to: `<a href=${lastThoughtPath}>${endDate}</a>` })}
	</div>
	<div class="stabilize-when-animate border-t overflow-hidden">
		{#key $t('thoughts.written-things')}
			<h1 in:fly={transitionIn()} out:fly={transitionOut()} class="font-sans italic text-2xl md:text-3xl lg:text-4xl px-2 md:px-4 lg:px-8 mb-4">
				{$t('thoughts.written-things')}
			</h1>
		{/key}
	</div>
	<div class="font-sans px-2 md:px-4 lg:px-8 pb-8 [&_a]:animated-underline-sage-green [&_a]:font-bold">
		{#each sortedYears as year}
			{#key year}
				<div in:fly={transitionIn()} out:fly={transitionOut()}>
					<p class="sticky top-0 text-3xl mb-8 mt-16 bg-linear-to-b from-bridal-heath-50 from-90% to-transparent z-10">
						{year}
					</p>
					<ul class="grid md:grid-cols-2">
						{#each decoratedThoughtsByYear[year] as thought}
							<li class="mb-4">
								<ThoughtPermalink {thought}>
									{thought.title}
								</ThoughtPermalink>
								<br />
								{formatToLocaleAsLong(new Date(thought._firstPublishedAt))}
							</li>
						{/each}
					</ul>
				</div>
			{/key}
		{/each}
	</div>
</div>
